import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  concat,
  NormalizedCacheObject,
} from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { apiConfig } from "../../configs/api";
import { readSelectedCustomerId } from "../../utils/customer_selection_storage";
import { AuthenticationService } from "./authentication";
import { perspectiveCustomerId } from "./customer_context";

export const getNewApiClient = (
  authenticationService: AuthenticationService = AuthenticationService.getInstance(),
): ApolloClient<NormalizedCacheObject> => {
  // Best recreated a new one at every authentication event
  // to clear the in memory cache. The service takes care of that.

  const {
    perspectiveServer: { protocol, host, graphqlPath },
  } = apiConfig;
  const uri = `${protocol}${host}${graphqlPath}`;
  const clientCustomerId = readSelectedCustomerId() || "";

  const httpLink = new HttpLink({
    uri: uri,
    fetch: authenticationService.awsGraphqlFetch,
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: uri,
      connectionParams: async () => {
        // Wait until we have a valid idToken before proceeding
        let idToken;
        while (!idToken) {
          idToken = AuthenticationService.getIdToken();
          if (!idToken) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }
        return {
          authToken: `${idToken || ""}|${clientCustomerId}`,
        };
      },
    }),
  );

  const atomicHeaderMiddleware = new ApolloLink((operation, forward) => {
    const { headers = {}, atomicCustomerId } = operation.getContext();
    operation.setContext({
      headers: {
        ...headers,
        // NOTE: set your custom headers here,
        // and check the backend for CORS headers as well.
        "X-Atomic-CustomerId": perspectiveCustomerId(
          atomicCustomerId,
          clientCustomerId,
        ),
      },
    });
    return forward(operation);
  });

  const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
    link: ApolloLink.split(
      // split based on operation type
      ({ query, operationName }) => {
        const definition = query.definitions[0];
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      wsLink,
      concat(atomicHeaderMiddleware, httpLink),
    ),
    cache: new InMemoryCache(),
  });

  return client;
};
